import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {create} from '@bufbuild/protobuf';
import type {PlayerStateUpdate} from '../../generated/bay_session_pb';
import {PlayerReadiness} from '../../generated/bay_session_shared_pb';
import {
  ConfirmReadyToSwingRequestSchema, RespondToConfirmationRequestSchema
} from '../../generated/player_bay_session_pb';
import {PlayerBaySession_Client} from '../constants';
import {StoreContext} from '../components/StoreContextProvider';
import type {LegacyConfirmationEvent} from './useStreamKioskUpdates';
import {confirmationPromptOwed} from '../utils/confirmationPromptOwed';
import ServiceClient from '../utils/ServiceClient';
import {
  ACTIVE_PLAYER_CONFIRMATION_MAX_AGE_MS, activePlayerConfirmationOwnerEquals, clearPersistedActivePlayerConfirmation,
  persistActivePlayerConfirmation, readPersistedActivePlayerConfirmation, type ActivePlayerConfirmationOwner
} from '../services/activePlayerConfirmationStorage';
import {
  confirmReadyToSwingAndRequirePlay, isReadyToSwingNotConfirmedError, isStaleConfirmReadyToSwingError
} from '../grpc/confirmReadyToSwing';

type Props = {
  bayPlayerState: PlayerStateUpdate | undefined, legacyConfirmationEvent: LegacyConfirmationEvent | undefined,
  legacySessionId: string | undefined
};

type RuntimeOwner = ActivePlayerConfirmationOwner & { key: string, stationId: bigint, authSessionToken: string };

type FlowState = {
  ownerKey: string | null, status: 'idle' | 'pending' | 'submitting', timestamp: number
};

const IDLE_FLOW: FlowState = {ownerKey: null, status: 'idle', timestamp: 0};
const E2E_OWNER_KEY = '__e2e_active_player_confirmation__';

const readE2ETimestamp = (): number => {
  if (process.env.NODE_ENV === 'production' || typeof window === 'undefined') return 0;

  const params = new URLSearchParams(window.location?.search || '');
  if (params.get('e2e') !== '1') return 0;

  const timestamp = (window as any).__TEST_ACTIVE_PLAYER_CONFIRMATION_TIMESTAMP__;
  return typeof timestamp === 'number' && timestamp > 0 ? timestamp : 0;
};

const timestampFromPlayerState = (update: PlayerStateUpdate): number => {
  return update.sentAt ? Number(update.sentAt.seconds) * 1000 + Math.floor(update.sentAt.nanos / 1e6) : Date.now();
};

const useActivePlayerConfirmationFlow = ({bayPlayerState, legacyConfirmationEvent, legacySessionId}: Props) => {
  const Context = useContext(StoreContext);
  const {
    session, user, sessionBackend, bayPlayerJwt, bayPlayerReadiness, setActivePlayerConfirmationModalVisible,
    setLoadingOverlay, setCancelModalConfig
  } = Context;
  const [flow, setFlow] = useState<FlowState>(IDLE_FLOW);
  const [readinessBlockedMessage, setReadinessBlockedMessage] = useState<string | null>(null);
  const flowRef = useRef(flow);
  const ownerRef = useRef<RuntimeOwner | null>(null);
  const mountedRef = useRef(false);
  const generationRef = useRef(0);
  const persistenceEpochRef = useRef(0);
  const wasBayConfirmationOwedRef = useRef(false);
  const restoreSuppressedOwnerKeyRef = useRef('');
  const lastLegacyEventRef = useRef('');
  const displayedLegacyEventRef = useRef('');
  const respondingRef = useRef(false);
  const e2eTimestamp = readE2ETimestamp();

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      generationRef.current += 1;
      persistenceEpochRef.current += 1;
      respondingRef.current = false;
      ownerRef.current = null;
    };
  }, []);

  const runtimeOwner = useMemo<RuntimeOwner | null>(() => {
    const authSessionToken = session?.token ?? '';
    const userId = user?.userId ?? session?.userId ?? 0n;
    if (!authSessionToken || userId === 0n) return null;

    const authSessionStart = String(session?.sessionStart ?? 0n);
    const sessionId = sessionBackend === 'legacy' ? legacySessionId ?? '' : String(bayPlayerState?.state?.currentStationId ?? 0n);
    if (!sessionId || sessionId === '0') return null;

    const owner: ActivePlayerConfirmationOwner = {
      backend: sessionBackend, sessionId, userId: String(userId), authSessionStart
    };

    return {
      ...owner, key: [sessionBackend, sessionId, userId, authSessionStart, authSessionToken].join(':'),
      authSessionToken, stationId: sessionBackend === 'bay-session' ? BigInt(sessionId) : 0n
    };
  }, [session?.token, session?.userId, session?.sessionStart, user?.userId, sessionBackend, legacySessionId, bayPlayerState?.state?.currentStationId]);

  const publishFlow = useCallback((next: FlowState): void => {
    flowRef.current = next;
    setFlow(next);
    setActivePlayerConfirmationModalVisible(next.timestamp);
  }, [setActivePlayerConfirmationModalVisible]);

  const clearOwnerPersistence = useCallback((owner: ActivePlayerConfirmationOwner, timestamp?: number): void => {
    void clearPersistedActivePlayerConfirmation({...owner, timestamp}).catch(error => {
      console.error('[ActivePlayerConfirmationFlow] failed to clear persisted confirmation', error);
    });
  }, []);

  const closeForOwner = useCallback((owner: RuntimeOwner): void => {
    persistenceEpochRef.current += 1;
    const ownedFlow = flowRef.current.ownerKey === owner.key ? flowRef.current : undefined;
    const ownedTimestamp = ownedFlow?.timestamp;
    if (ownedTimestamp !== undefined) publishFlow(IDLE_FLOW);
    clearOwnerPersistence(owner, ownedTimestamp);
  }, [clearOwnerPersistence, publishFlow]);

  const persistForOwner = useCallback((owner: RuntimeOwner, timestamp: number, epoch: number): void => {
    const generation = generationRef.current;

    void persistActivePlayerConfirmation(owner, timestamp).then(() => {
      if (!mountedRef.current) return;
      if (generation !== generationRef.current || epoch !== persistenceEpochRef.current) {
        clearOwnerPersistence(owner, timestamp);
      }

    }).catch(error => {
      console.error('[ActivePlayerConfirmationFlow] failed to persist confirmation', error);
    });
  }, [clearOwnerPersistence]);

  const openForOwner = useCallback((owner: RuntimeOwner, timestamp: number): void => {
    const epoch = persistenceEpochRef.current + 1;
    persistenceEpochRef.current = epoch;
    setReadinessBlockedMessage(null);
    publishFlow({ownerKey: owner.key, status: 'pending', timestamp});
    persistForOwner(owner, timestamp, epoch);
  }, [persistForOwner, publishFlow]);

  useEffect(() => {
    setReadinessBlockedMessage(null);
  }, [runtimeOwner?.key, bayPlayerReadiness]);

  useEffect(() => {
    const previousOwner = ownerRef.current;
    const ownerChanged = previousOwner?.key !== runtimeOwner?.key;
    if (!ownerChanged && !e2eTimestamp) return;

    generationRef.current += 1;
    persistenceEpochRef.current += 1;
    respondingRef.current = false;
    wasBayConfirmationOwedRef.current = false;
    restoreSuppressedOwnerKeyRef.current = '';
    lastLegacyEventRef.current = '';
    displayedLegacyEventRef.current = '';

    if (flowRef.current.status === 'submitting') setLoadingOverlay(false);

    if (previousOwner) {
      const previousFlow = flowRef.current.ownerKey === previousOwner.key ? flowRef.current : undefined;
      clearOwnerPersistence(previousOwner, previousFlow?.timestamp);
    }

    ownerRef.current = runtimeOwner;
    publishFlow(IDLE_FLOW);

    if (e2eTimestamp) {
      publishFlow({ownerKey: E2E_OWNER_KEY, status: 'pending', timestamp: e2eTimestamp});
      return;
    }

    if (!runtimeOwner) return;

    const generation = generationRef.current;
    void readPersistedActivePlayerConfirmation().then(record => {
      if (!record || generation !== generationRef.current || ownerRef.current?.key !== runtimeOwner.key) return;

      const age = Math.abs(Date.now() - record.timestamp);
      if (activePlayerConfirmationOwnerEquals(record, runtimeOwner) && age <= ACTIVE_PLAYER_CONFIRMATION_MAX_AGE_MS && restoreSuppressedOwnerKeyRef.current !== runtimeOwner.key && flowRef.current.status === 'idle') {
        publishFlow({
          ownerKey: runtimeOwner.key, status: 'pending', timestamp: record.timestamp
        });
        return;
      }

      clearOwnerPersistence(record, record.timestamp);

    }).catch(error => {
      console.error('[ActivePlayerConfirmationFlow] failed to restore confirmation', error);
    });
  }, [runtimeOwner?.key, e2eTimestamp, clearOwnerPersistence, publishFlow, setLoadingOverlay]);

  useEffect(() => {
    const owner = runtimeOwner;
    const state = bayPlayerState?.state;
    if (e2eTimestamp || owner?.backend !== 'bay-session' || !state || state.userId !== BigInt(owner.userId)) {
      wasBayConfirmationOwedRef.current = false;
      return;
    }

    const confirmationOwed = confirmationPromptOwed(state);
    if (confirmationOwed && !wasBayConfirmationOwedRef.current) {
      openForOwner(owner, timestampFromPlayerState(bayPlayerState));
    } else if (!confirmationOwed) {
      restoreSuppressedOwnerKeyRef.current = owner.key;
      if (flowRef.current.ownerKey === owner.key) closeForOwner(owner);
    }

    wasBayConfirmationOwedRef.current = confirmationOwed;
  }, [bayPlayerState, runtimeOwner?.key, e2eTimestamp, closeForOwner, openForOwner]);

  useEffect(() => {
    const owner = runtimeOwner;
    const event = legacyConfirmationEvent;
    if (e2eTimestamp || owner?.backend !== 'legacy' || !event) return;
    if (event.sessionId !== owner.sessionId || event.authSessionToken !== owner.authSessionToken || event.subjectUserId !== owner.userId) return;

    const removedUserId = event.kind === 'removed' ? event.userId : '';
    const eventKey = [event.kind, event.sessionId, event.timestamp, removedUserId].join(':');
    const isNewEvent = lastLegacyEventRef.current !== eventKey;
    if (isNewEvent) lastLegacyEventRef.current = eventKey;

    if (event.kind === 'removed') {
      if (!isNewEvent || (event.userId !== 0 && String(event.userId) !== owner.userId)) return;
      displayedLegacyEventRef.current = '';
      closeForOwner(owner);
      return;
    }

    if (isNewEvent) {
      openForOwner(owner, event.timestamp > 0 ? event.timestamp : Date.now());
    }

    if (displayedLegacyEventRef.current === eventKey) return;
    displayedLegacyEventRef.current = eventKey;
    void ServiceClient.fullSwingSessionParticipationConfirmationDisplayed(event.sessionId, owner.authSessionToken).catch(error => {
      if (displayedLegacyEventRef.current === eventKey) displayedLegacyEventRef.current = '';
      console.error('[ActivePlayerConfirmationFlow] failed to acknowledge displayed confirmation', error);
    });
  }, [legacyConfirmationEvent, runtimeOwner?.key, e2eTimestamp, closeForOwner, openForOwner]);

  const confirm = useCallback(async (): Promise<void> => {
    const owner = ownerRef.current;
    const currentFlow = flowRef.current;
    if (
      !owner || !runtimeOwner || owner.key !== runtimeOwner.key ||
      currentFlow.ownerKey !== owner.key || currentFlow.status !== 'pending' ||
      respondingRef.current
    ) return;

    respondingRef.current = true;
    setReadinessBlockedMessage(null);
    const submittingFlow: FlowState = {...currentFlow, status: 'submitting'};
    flowRef.current = submittingFlow;
    setFlow(submittingFlow);
    setLoadingOverlay(true);
    const generation = generationRef.current;

    try {
      if (owner.backend === 'legacy') {
        await ServiceClient.fullSwingSessionParticipationConfirmationRespond(owner.authSessionToken);

      } else {
        if (!bayPlayerJwt || owner.stationId === 0n) {
          throw new Error('Session not ready — please reload the app.');
        }

        const auth = {headers: {Authorization: `Bearer ${bayPlayerJwt}`}};
        if (bayPlayerReadiness === PlayerReadiness.CONFIRM) {
          await confirmReadyToSwingAndRequirePlay(() => {
            return PlayerBaySession_Client.confirmReadyToSwing(
              create(ConfirmReadyToSwingRequestSchema, {stationId: owner.stationId}), auth,
            );
          });
        }
        await PlayerBaySession_Client.respondToConfirmation(
          create(RespondToConfirmationRequestSchema, {stationId: owner.stationId, accepted: true}), auth,
        );
      }

      if (
        generation === generationRef.current && ownerRef.current?.key === owner.key &&
        flowRef.current === submittingFlow
      ) {
        closeForOwner(owner);
      } else {
        clearOwnerPersistence(owner, currentFlow.timestamp);
      }
    } catch (error: unknown) {
      if (
        generation === generationRef.current && ownerRef.current?.key === owner.key &&
        flowRef.current === submittingFlow
      ) {
        const retryableFlow: FlowState = {...currentFlow, status: 'pending'};
        flowRef.current = retryableFlow;
        setFlow(retryableFlow);

        if (isStaleConfirmReadyToSwingError(error)) return;

        if (isReadyToSwingNotConfirmedError(error)) {
          setReadinessBlockedMessage(
            error instanceof Error ? error.message : 'Player is not ready to play yet.'
          );
          return;
        }

        const message = error instanceof Error && error.message
          ? error.message
          : 'We could not confirm your turn. Please try again.';
        setCancelModalConfig({
          visible: true,
          title: 'ERROR',
          subtitle: message,
          rightBtnText: 'OK',
          onResume: () => setCancelModalConfig(previous => ({...previous, visible: false})),
          onClose: () => setCancelModalConfig(previous => ({...previous, visible: false})),
        });
      }
    } finally {
      if (generation === generationRef.current && ownerRef.current?.key === owner.key) {
        setLoadingOverlay(false);
        respondingRef.current = false;
      }
    }
  }, [runtimeOwner, bayPlayerJwt, bayPlayerReadiness, clearOwnerPersistence, closeForOwner, setCancelModalConfig, setLoadingOverlay]);

  const requestClose = useCallback((): void => {
    // Server confirmation state is authoritative. Android back/hardware-close
    // must not hide an owed prompt and leave the player unable to continue.
    if (flowRef.current.ownerKey === E2E_OWNER_KEY) publishFlow(IDLE_FLOW);
  }, [publishFlow]);

  const visibleTimestamp = e2eTimestamp
    ? (flow.ownerKey === E2E_OWNER_KEY ? flow.timestamp : 0)
    : (runtimeOwner && flow.ownerKey === runtimeOwner.key ? flow.timestamp : 0);

  return {
    visibleTimestamp,
    isSubmitting: flow.status === 'submitting',
    readinessBlockedMessage,
    confirm,
    requestClose,
  };
};

export default useActivePlayerConfirmationFlow;
